import { CalendarGridColumnName } from '../../../grid/types/column.types';
import {
  ConfigureOptionsParams,
  DescriptionFilterType,
  DoubleDescriptionFilterModel,
  DoubleDescriptionFilterOperator,
  FilterOptions,
  IFilterComparator,
  SingleDescriptionFilterModel,
  FilterPassParams
} from '../filter-types';
import { DefaultComparator } from './comparators/default-comparator';
import { FilterResultFacade } from '../filter-result-facade';
import { FilterHandlerBase } from './base/filter-handler-base';

export class DescriptionFilterHandler extends FilterHandlerBase {
  private comparator: IFilterComparator<
    SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  >;
  static getReadableConditionType(type: DescriptionFilterType): string {
    switch (type) {
      case DescriptionFilterType.EndsWith:
        return 'ends with';
      case DescriptionFilterType.StartsWith:
        return 'starts with';
      case DescriptionFilterType.NotContains:
        return 'not contains';
      case DescriptionFilterType.NotEqual:
        return 'not equal';
      default:
        return type;
    }
  }

  static getReadableOperator(
    operator: DoubleDescriptionFilterOperator
  ): string {
    if (!operator) return '';
    return operator === DoubleDescriptionFilterOperator.AND ? 'and' : 'or';
  }

  public static isDoubleDescriptionModel(model: any): boolean {
    if (!model) return false;
    const sdModelType: SingleDescriptionFilterModel = {
      filter: '',
      filterType: undefined,
      type: undefined
    };
    const modelType: DoubleDescriptionFilterModel = {
      condition1: sdModelType,
      condition2: sdModelType,
      operator: DoubleDescriptionFilterOperator.AND
    };
    for (const key in modelType) {
      if (
        Object.prototype.hasOwnProperty.call(key, modelType) &&
        !Object.prototype.hasOwnProperty.call(key, model)
      ) {
        return false;
      }
    }
    return true;
  }

  public static getDoubleDescriptionLabel(
    dModel: DoubleDescriptionFilterModel
  ): string {
    return `${DescriptionFilterHandler.getReadableConditionType(
      dModel.condition1.type
    )} "${
      dModel.condition1.filter
    }" ${DescriptionFilterHandler.getReadableOperator(
      dModel.operator
    )} ${DescriptionFilterHandler.getReadableConditionType(
      dModel.condition2.type
    )} "${dModel.condition2.filter}"`;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  get name(): string {
    return CalendarGridColumnName.Description;
  }

  configureFilterOptions(params: ConfigureOptionsParams): void {
    return;
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return null;
  }

  hasValidDiff(
    options: FilterOptions,
    grid: Partial<SingleDescriptionFilterModel | DoubleDescriptionFilterModel>,
    store: SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  ): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(grid, store);
  }

  getValidDiffComparator(
    options: FilterOptions
  ): IFilterComparator<
    SingleDescriptionFilterModel | DoubleDescriptionFilterModel
  > {
    if (!this.comparator) {
      this.comparator = new DefaultComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  protected getDataFromParams(
    params: FilterPassParams
  ): { id: string; description: string } {
    return params.deal;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade) {
    let descriptionFilterPassed = true;
    const description = params.filters[this.name];
    const data = this.getDataFromParams(params);
    if (description) {
      const isDoubleDescription = Object.prototype.hasOwnProperty.call(
        description.model,
        'operator'
      );
      if (isDoubleDescription) {
        const doubleModel: DoubleDescriptionFilterModel = description.model;
        const single1FilterPassed = this.filterSingleDescriptionModel(
          doubleModel.condition1,
          data.description
        );
        const single2FilterPassed = this.filterSingleDescriptionModel(
          doubleModel.condition2,
          data.description
        );
        if (doubleModel.operator === DoubleDescriptionFilterOperator.AND) {
          descriptionFilterPassed = single1FilterPassed && single2FilterPassed;
        } else if (
          doubleModel.operator === DoubleDescriptionFilterOperator.OR
        ) {
          descriptionFilterPassed = single1FilterPassed || single2FilterPassed;
        }
      } else {
        const singleModel: SingleDescriptionFilterModel = description.model;
        descriptionFilterPassed = this.filterSingleDescriptionModel(
          singleModel,
          data.description
        );
      }
    }
    result.applyDealFilerResult(data.id, this.name, descriptionFilterPassed);

    return descriptionFilterPassed;
  }

  filterSingleDescriptionModel(
    model: SingleDescriptionFilterModel,
    dealDescription: string
  ): boolean {
    dealDescription = dealDescription ? dealDescription.toUpperCase() : '';
    const filter = model.filter ? model.filter.toUpperCase() : '';
    switch (model.type) {
      case DescriptionFilterType.Equals:
        return dealDescription === filter;
      case DescriptionFilterType.NotEqual:
        return dealDescription !== filter;
      case DescriptionFilterType.Contains:
        return dealDescription.indexOf(filter) !== -1;
      case DescriptionFilterType.NotContains:
        return dealDescription.indexOf(filter) === -1;
      case DescriptionFilterType.StartsWith:
        return dealDescription.startsWith(filter);
      case DescriptionFilterType.EndsWith:
        return dealDescription.endsWith(filter);
    }
  }
}
